import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Plus, Trash2 as Trash, FileText as FilePdf, Save as FloppyDisk, X } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { toast } from "sonner";

const DEPARTMENTS = ["Mold", "Frame", "GFRC", "Finish", "Fiberglass"];
const BLOCK_TYPES = [
  { type: "kpi_hours",       title: "KPI · Hours" },
  { type: "kpi_sf",          title: "KPI · Production SF" },
  { type: "kpi_cost",        title: "KPI · Material Cost" },
  { type: "kpi_sfph",        title: "KPI · SF/Hour" },
  { type: "dept_mold",       title: "Department · Mold" },
  { type: "dept_frame",      title: "Department · Frame" },
  { type: "dept_gfrc",       title: "Department · GFRC" },
  { type: "dept_finish",     title: "Department · Finish" },
  { type: "dept_fiberglass", title: "Department · Fiberglass" },
  { type: "completion",      title: "Completion Rates" },
];

function BlockPreview({ block, data }) {
  if (block.type.startsWith("kpi_") && data?.kpis) {
    const map = {
      kpi_hours: { label: "Total Hours",   value: data.kpis.total_hours,   unit: "hrs" },
      kpi_sf:    { label: "Production SF", value: data.kpis.production_sf, unit: "sf" },
      kpi_cost:  { label: "Material Cost", value: `$${data.kpis.material_cost?.toLocaleString()}`, unit: "" },
      kpi_sfph:  { label: "SF / Hour",     value: data.kpis.sf_per_hour,   unit: "sf/h" },
    };
    const k = map[block.type];
    return (
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">{k.label}</div>
        <div className="font-heading font-bold text-3xl mt-2">{k.value} <span className="text-xs text-muted-foreground">{k.unit}</span></div>
      </div>
    );
  }
  if (block.type.startsWith("dept_") && data?.depts) {
    const name = block.type.replace("dept_", "");
    const d = data.depts.find((x) => x.department.toLowerCase() === name);
    if (!d) return null;
    return (
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#00D4FF]">{d.department}</div>
        <div className="grid grid-cols-5 gap-1.5 mt-3">
          {["Completed","In Progress","Production Repair","New NCR","On Hold"].map((s) => (
            <div key={s} className="bg-[#0A0B10] border border-border p-2">
              <div className="text-[8px] uppercase tracking-widest text-muted-foreground truncate">{s}</div>
              <div className="font-mono text-sm mt-1">{d.status_counts?.[s] || 0}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
          <div><span className="text-muted-foreground">Hrs:</span> <span className="font-mono">{d.hours}</span></div>
          <div><span className="text-muted-foreground">SF:</span> <span className="font-mono">{d.sf}</span></div>
          <div><span className="text-muted-foreground">$:</span> <span className="font-mono">{d.cost?.toLocaleString()}</span></div>
        </div>
      </div>
    );
  }
  if (block.type === "completion" && data?.completion) {
    return (
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">Completion Rates</div>
        {data.completion.map((c) => (
          <div key={c.department} className="mb-2">
            <div className="flex justify-between text-xs mb-1">
              <span>{c.department}</span><span className="font-mono text-[#00D4FF]">{c.pct}%</span>
            </div>
            <div className="h-1 bg-[#1A1C28]"><div className="h-full bg-[#00D4FF]" style={{ width: `${c.pct}%` }} /></div>
          </div>
        ))}
      </div>
    );
  }
  return <div className="text-xs text-muted-foreground">{block.title}</div>;
}

export default function Builder() {
  const [blocks, setBlocks] = useState([]);
  const [layouts, setLayouts] = useState([]);
  const [name, setName] = useState("Untitled Layout");
  const [currentId, setCurrentId] = useState(null);
  const [data, setData] = useState(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    (async () => {
      const [k, depts, c, ll] = await Promise.all([
        api.get("/dashboard/kpis", { params: { preset: "30d" } }),
        api.get("/dashboard/departments", { params: { preset: "30d" } }),
        api.get("/completion-rates"),
        api.get("/builder/layouts"),
      ]);
      setData({ kpis: k.data, depts: depts.data, completion: c.data });
      setLayouts(ll.data);
    })();
  }, []);

  const addBlock = (type) => {
    const meta = BLOCK_TYPES.find((b) => b.type === type);
    setBlocks((b) => [...b, { id: `b-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, type, title: meta?.title || type }]);
  };

  const removeBlock = (id) => setBlocks((b) => b.filter((x) => x.id !== id));

  const saveLayout = async () => {
    const items = blocks.map((b, i) => ({
      id: b.id, type: b.type, title: b.title,
      department: b.type.startsWith("dept_") ? b.type.replace("dept_", "") : null,
      x: (i % 2) * 6, y: Math.floor(i / 2) * 2, w: 6, h: 2,
    }));
    try {
      if (currentId) {
        await api.put(`/builder/layouts/${currentId}`, { name, items });
        toast.success("Layout updated");
      } else {
        const { data: saved } = await api.post("/builder/layouts", { name, items });
        setCurrentId(saved.id);
        toast.success("Layout saved");
      }
      const ll = await api.get("/builder/layouts");
      setLayouts(ll.data);
    } catch {
      toast.error("Save failed");
    }
  };

  const loadLayout = (l) => {
    setName(l.name);
    setCurrentId(l.id);
    setBlocks(l.items.map((i) => ({ id: i.id, type: i.type, title: i.title })));
  };

  const deleteLayout = async (id) => {
    await api.delete(`/builder/layouts/${id}`);
    const ll = await api.get("/builder/layouts");
    setLayouts(ll.data);
    if (currentId === id) { setCurrentId(null); setBlocks([]); setName("Untitled Layout"); }
    toast.success("Deleted");
  };

  const exportPdf = async () => {
    if (!canvasRef.current) return;
    toast.info("Rendering PDF…");
    const canvas = await html2canvas(canvasRef.current, {
      backgroundColor: "#0A0B10",
      scale: 2,
      useCORS: true,
    });
    const img = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const ratio = Math.min(pageWidth / canvas.width, pageHeight / canvas.height);
    const w = canvas.width * ratio;
    const h = canvas.height * ratio;
    pdf.addImage(img, "PNG", (pageWidth - w) / 2, (pageHeight - h) / 2, w, h);
    pdf.save(`${name.replace(/\s+/g, "_")}.pdf`);
    toast.success("PDF downloaded");
  };

  return (
    <div className="space-y-6" data-testid="builder-page">
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="text-[11px] tracking-[0.35em] uppercase text-[#00D4FF]">04 · Builder</div>
          <h1 className="font-heading font-bold text-4xl tracking-tight mt-2">Dashboard Builder</h1>
          <p className="text-sm text-muted-foreground mt-1">Compose department layouts and export to PDF</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            data-testid="layout-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="bg-[#12141D] border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#00D4FF]"
          />
          <button data-testid="builder-save"
            onClick={saveLayout}
            className="bg-transparent border border-border hover:border-[#00D4FF] px-3 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2">
            <FloppyDisk size={14} /> Save
          </button>
          <button data-testid="builder-export-pdf"
            onClick={exportPdf}
            className="bg-[#00D4FF] text-[#0A0B10] font-semibold px-3 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 hover:bg-[#00B8D4]">
            <FilePdf size={14} /> Export PDF
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4">
        {/* Palette + saved */}
        <aside className="space-y-4">
          <div className="bg-[#12141D] border border-border p-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">Add Block</div>
            <div className="flex flex-col gap-1.5">
              {BLOCK_TYPES.map((b) => (
                <button
                  key={b.type}
                  data-testid={`builder-add-${b.type}`}
                  onClick={() => addBlock(b.type)}
                  className="text-left text-xs border border-border bg-[#0A0B10] hover:border-[#00D4FF] hover:text-[#00D4FF] px-2.5 py-2 flex items-center gap-2 transition-colors"
                >
                  <Plus size={12} /> {b.title}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-[#12141D] border border-border p-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">Saved Layouts</div>
            {layouts.length === 0 && <div className="text-xs text-muted-foreground">None yet</div>}
            <div className="flex flex-col gap-1.5">
              {layouts.map((l) => (
                <div key={l.id} className="flex items-center gap-1">
                  <button
                    onClick={() => loadLayout(l)}
                    className={`flex-1 text-left text-xs border px-2.5 py-2 transition-colors
                      ${currentId === l.id ? "border-[#00D4FF] text-[#00D4FF]" : "border-border hover:border-[#00D4FF]/60"}`}
                  >
                    {l.name}
                  </button>
                  <button onClick={() => deleteLayout(l.id)} className="p-2 text-muted-foreground hover:text-red-400">
                    <Trash size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* Canvas */}
        <div className="bg-[#0A0B10] border border-dashed border-border p-4 min-h-[70vh]" data-testid="builder-canvas-wrapper">
          <div ref={canvasRef} className="grid grid-cols-1 md:grid-cols-2 gap-3 p-2 bg-[#0A0B10]">
            {blocks.length === 0 && (
              <div className="col-span-2 text-center py-24 text-muted-foreground text-xs tracking-[0.3em] uppercase">
                Click a block on the left to start building →
              </div>
            )}
            {blocks.map((b) => (
              <div
                key={b.id}
                data-testid={`builder-block-${b.type}`}
                className={`bg-[#12141D] border border-border p-4 relative group
                  ${b.type === "completion" ? "md:col-span-2" : ""}`}
              >
                <button
                  onClick={() => removeBlock(b.id)}
                  className="absolute top-2 right-2 text-muted-foreground hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X size={14} />
                </button>
                <BlockPreview block={b} data={data} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
